import { Skeleton } from '@/components/ui/skeleton';

import { cn } from '@/lib/utils';

/**
 * Route-level skeleton for the responses page. The mobile/desktop split is CSS-only
 * (651px, matching MOBILE_BREAKPOINT_PX) because this also renders from the route's
 * server `loading.tsx`, where no window width exists to branch on.
 */
export function FormResponsesSkeleton() {
	return (
		<>
			<div className="min-[651px]:hidden">
				<FormResponsesMobileSkeleton />
			</div>
			<div className="hidden min-[651px]:block">
				<FormResponsesDesktopSkeleton />
			</div>
		</>
	);
}

export function FormResponsesMobileSkeleton({
	offsetForNavbar = true,
}: {
	/** False when rendering inside the page shell, which already pads for the mobile navbar. */
	offsetForNavbar?: boolean;
}) {
	return (
		<main
			className={cn(
				'flex h-full min-h-screen w-full flex-col bg-white',
				offsetForNavbar && 'pt-9'
			)}
		>
			{/* Header: back button, form name, options */}
			<div className="flex items-center justify-between border-b border-border p-4 py-3">
				<div className="flex min-w-0 flex-1 items-center gap-3">
					<Skeleton className="h-8 w-8 flex-shrink-0" />
					<Skeleton className="h-6 w-40" />
				</div>
				<Skeleton className="h-8 w-8 flex-shrink-0" />
			</div>

			{/* Search bar + icon buttons, then stage tabs */}
			<div className="border-b border-border px-4 py-3">
				<div className="mb-3 flex items-center gap-2">
					<Skeleton className="h-9 flex-1" />
					<Skeleton className="h-9 w-9 flex-shrink-0" />
					<Skeleton className="h-9 w-9 flex-shrink-0" />
					<Skeleton className="h-9 w-9 flex-shrink-0" />
				</div>
				<div className="flex items-center gap-2">
					<Skeleton className="h-9 w-20 rounded-md" />
					<Skeleton className="h-9 w-24 rounded-md" />
					<Skeleton className="h-9 w-24 rounded-md" />
				</div>
			</div>

			{/* Candidate cards */}
			<CandidateCardsSkeleton />
		</main>
	);
}

function FormResponsesDesktopSkeleton() {
	return (
		<main className="flex h-full min-h-screen w-full flex-col bg-accentLight">
			{/* Header Section */}
			<div className="flex items-center justify-between border-border px-6 py-3.5">
				<div className="flex items-center gap-3">
					<Skeleton className="h-6 w-6" />
					<div className="flex items-center gap-2">
						<Skeleton className="h-6 w-32" />
						<Skeleton className="h-5 w-20" />
					</div>
				</div>
				<div className="flex items-center gap-3">
					<Skeleton className="h-8 w-8" />
					<Skeleton className="h-8 w-8" />
					<Skeleton className="h-8 w-8" />
					<Skeleton className="h-8 w-8" />
				</div>
			</div>

			{/* Main Content */}
			<TableSkeleton />
		</main>
	);
}

export function TableSkeleton() {
	return (
		<div className="mx-6 flex h-full flex-1 overflow-hidden rounded-t-lg border border-b-0 border-border bg-white">
			<div className="flex min-w-0 flex-1 flex-col">
				{/* Tab Navigation and Controls */}
				<div className="flex items-center justify-between">
					{/* Tabs */}
					<div className="flex items-center gap-2 px-4 py-3">
						<div className="flex items-center gap-2">
							<Skeleton className="h-9 w-16 rounded-md" />
							<Skeleton className="h-9 w-20 rounded-md" />
							<Skeleton className="w-18 h-8 rounded-md" />
							<Skeleton className="h-6 w-6 rounded-full" />
						</div>
					</div>

					{/* Search and Controls */}
					<div className="flex shrink-0 items-center gap-4 px-2 pr-4">
						<div className="flex items-center gap-2">
							<Skeleton className="h-8 w-8" />
							<Skeleton className="h-8 w-40" />
							<Skeleton className="h-8 w-24" />
						</div>
					</div>
				</div>

				{/* Table Skeleton */}
				<TableRowsSkeleton />
			</div>
		</div>
	);
}

export function CandidateCardsSkeleton() {
	return (
		<div className="flex-1 p-4">
			<div className="overflow-hidden rounded-lg border border-border shadow-sm">
				{Array.from({ length: 8 }).map((_, index) => (
					<div
						key={index}
						className={cn('bg-white p-4', index < 7 && 'border-b border-border')}
					>
						<Skeleton className="mb-2 h-4 w-32" />
						<Skeleton className="h-3 w-24" />
					</div>
				))}
			</div>
		</div>
	);
}

export function TableRowsSkeleton() {
	return (
		<div className="flex-1 overflow-hidden">
			{/* Table Rows */}
			<div className="flex-1 overflow-y-auto">
				{Array.from({ length: 12 }).map((_, index) => (
					<div
						key={index}
						className="flex h-12 items-center border-b border-border/70 px-3"
					>
						{/* Checkbox */}
						<div className="flex w-10 items-center justify-center">
							<Skeleton className="h-4 w-4" />
						</div>

						{/* Row content */}
						<div className="flex flex-1 items-center gap-4 px-4">
							{/* Applicant name */}
							<div className="flex w-32 items-center">
								<Skeleton className="h-4 w-24" />
							</div>

							{/* Email */}
							<div className="flex w-40 items-center">
								<Skeleton className="h-4 w-32" />
							</div>

							{/* created on date */}
							<div className="flex w-28 items-center">
								<Skeleton className="h-4 w-20" />
							</div>

							{/* Status */}
							<div className="flex w-20 items-center">
								<Skeleton className="h-4 w-16" />
							</div>

							{/* Second created on date */}
							<div className="flex w-28 items-center">
								<Skeleton className="h-4 w-20" />
							</div>

							{/* Labels */}
							<div className="flex w-24 items-center">
								<Skeleton className="h-4 w-24" />
							</div>

							{/* Custom field 1 */}
							<div className="flex w-16 items-center">
								<Skeleton className="h-4 w-12" />
							</div>

							{/* Custom field 2 */}
							<div className="flex w-20 items-center">
								<Skeleton className="h-4 w-12" />
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
}
